import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Order } from './useOrderQuery';

type Priority = 'normal' | 'urgent';

interface UseOrdersListResult {
  orders: Order[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  filterByStatus: (status: string | null) => Order[];
  filterBySearch: (searchTerm: string) => Order[];
}

export function useOrdersList(techId: string | null): UseOrdersListResult {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refetch = () => setRefreshTrigger(prev => prev + 1);

  useEffect(() => {
    if (!techId) {
      setOrders([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchOrders() {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('orders')
          .select(`
            *,
            profiles(*),
            status_config(*)
          `)
          .eq('assigned_to', techId)
          .order('priority', { ascending: false }) // urgent first
          .order('created_at', { ascending: false });

        if (fetchError) {
          if (!cancelled) {
            setError(fetchError.message);
            setOrders([]);
          }
          return;
        }

        if (!cancelled) {
          setOrders((data as Order[]) || []);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to fetch orders');
          setOrders([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchOrders();

    return () => {
      cancelled = true;
    };
  }, [techId, refreshTrigger]);

  const filterByStatus = useCallback((status: string | null): Order[] => {
    if (!status) return orders;
    return orders.filter(order => order.status === status);
  }, [orders]);

  const filterBySearch = useCallback((searchTerm: string): Order[] => {
    if (!searchTerm.trim()) return orders;
    const term = searchTerm.toLowerCase();
    return orders.filter(
      order =>
        order.customer_name?.toLowerCase().includes(term) ||
        order.device?.toLowerCase().includes(term) ||
        order.device_model?.toLowerCase().includes(term) ||
        order.profiles?.full_name?.toLowerCase().includes(term)
    );
  }, [orders]);

  return {
    orders,
    isLoading,
    error,
    refetch,
    filterByStatus,
    filterBySearch,
  };
}